// Dashboard Page - Vue d'ensemble de l'activité
// Principe: "10 secondes de compréhension" pour les artisans
// Mobile First avec responsive design

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Paper,
} from '@mui/material';
import { Refresh, ErrorOutline } from '@mui/icons-material';
import {
  PeriodSelector,
  KPICards,
  CashflowChart,
  HotlistImpayes,
  AlertJauges,
  DashboardFAB,
} from '../components/dashboard';
import { useDashboardStore } from '../stores/useDashboardStore';
import { useAuthStore } from '../stores/useAuthStore';
import { fetchDashboardSummary } from '../services/dashboardService';
import { DashboardSummary } from '../types/dashboard';
import { useNavigate } from 'react-router-dom';
import TrialBanner from '../components/TrialBanner';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { periode, setPeriode, getData, setData: setCacheData, isCacheValid, invalidateCache } = useDashboardStore();
  const { token, isAuthenticated, user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardSummary | null>(null);

  // Rediriger immédiatement vers /login si l'utilisateur n'est plus authentifié
  useEffect(() => {
    if (!isAuthenticated || !token || !user) {
      console.log('❌ Utilisateur non authentifié détecté - redirection vers /login');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, token, user, navigate]);

  // Charger les données du dashboard (avec cache)
  const loadDashboard = async (forceRefresh = false) => {
    console.log('🔄 loadDashboard démarré - forceRefresh:', forceRefresh);
    console.log('🔑 Token présent:', !!token);
    console.log('🔑 isAuthenticated:', isAuthenticated);
    console.log('👤 User ID:', user?.id);

    // Si pas de token ou pas d'utilisateur, ne rien faire (le useEffect va rediriger)
    if (!token || !isAuthenticated || !user) {
      console.log('❌ Pas de token/user - arrêt du chargement');
      setLoading(false);
      return;
    }

    const userId = user.id;

    // Vérifier le cache si on ne force pas le rafraîchissement
    if (!forceRefresh) {
      const cachedData = getData(periode, userId);
      const cacheIsValid = isCacheValid(periode, userId);

      if (cachedData && cacheIsValid) {
        console.log('✅ Utilisation des données en cache pour l\'utilisateur:', userId);
        setData(cachedData);
        setError(null);
        return;
      }
      console.log('⚠️ Cache invalide ou vide - chargement depuis l\'API');
    }

    try {
      console.log('⏳ Début de l\'appel API - période:', periode, 'utilisateur:', userId);
      setLoading(true);
      setError(null);
      const summary = await fetchDashboardSummary(periode);
      console.log('✅ Données reçues:', summary);
      setData(summary);
      setCacheData(periode, summary, userId); // Sauvegarder dans le cache avec l'userId
    } catch (err: any) {
      console.error('❌ Erreur lors du chargement du dashboard:', err);

      // Gestion d'erreur intelligente
      let errorMessage = 'Session expirée. Veuillez vous reconnecter.';

      if (err.message?.includes('Token')) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
      } else if (err.message?.includes('404')) {
        errorMessage = 'Service dashboard non disponible. Contactez le support technique.';
      } else if (err.message?.includes('500')) {
        errorMessage = 'Erreur serveur. Nos équipes ont été notifiées.';
      } else if (err.message?.includes('Network') || err.message?.includes('Failed to fetch')) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
      }

      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données initiales et quand la période change
  useEffect(() => {
    // Ne charger que si l'utilisateur est authentifié
    if (!user) {
      console.log('⚠️ Pas d\'utilisateur, attente...');
      return;
    }

    const userId = user.id;

    // Essayer d'abord de charger depuis le cache
    const cachedData = getData(periode, userId);
    if (cachedData && isCacheValid(periode, userId)) {
      console.log('✅ Données en cache valides trouvées pour la période:', periode, 'utilisateur:', userId);
      setData(cachedData);
      setError(null);
    } else {
      // Si pas de cache ou invalide, charger depuis l'API
      loadDashboard(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, user?.id]);

  // État de chargement
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
            }}
          >
            Tableau de bord
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Vue d'ensemble de votre activité
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Chargement de vos données...
          </Typography>
        </Box>
      </Container>
    );
  }

  // État d'erreur
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
            }}
          >
            Tableau de bord
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Vue d'ensemble de votre activité
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <ErrorOutline
            sx={{
              fontSize: 80,
              color: 'error.main',
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Impossible de charger les données
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => {
              // Si l'utilisateur n'est plus authentifié, rediriger vers login
              if (!isAuthenticated || !token || !user) {
                navigate('/login', { replace: true });
              } else {
                // Sinon, réessayer de charger les données
                loadDashboard(true);
              }
            }}
            size="large"
          >
            {!isAuthenticated || !token || !user ? 'Se reconnecter' : 'Réessayer'}
          </Button>
        </Paper>
      </Container>
    );
  }

  // Pas de données chargées (ne devrait pas arriver si loading est bien géré)
  if (!data) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Initialisation...
          </Typography>
        </Box>
      </Container>
    );
  }

  // État normal avec données
  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
            }}
          >
            Tableau de bord
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Vue d'ensemble de votre activité
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<Refresh />}
          onClick={() => loadDashboard(true)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Actualiser
        </Button>
      </Box>

      {/* Bannière période d'essai Premium */}
      <TrialBanner />

      {/* Sélecteur de période */}
      <PeriodSelector periode={periode} onChange={setPeriode} />

      {/* Contenu du dashboard */}
      <Box>
        {/* KPIs - 4 cartes */}
        <Box sx={{ mb: 3 }}>
          <KPICards kpis={data.kpis} />
        </Box>

        {/* Grille responsive pour le contenu principal */}
        <Grid container spacing={3}>
          {/* Graphique Cashflow - Pleine largeur sur mobile, 2/3 sur desktop */}
          <Grid item xs={12} md={8}>
            <CashflowChart data={data.cashflow} />
          </Grid>

          {/* Jauges TVA/Franchise - Pleine largeur sur mobile, 1/3 sur desktop */}
          <Grid item xs={12} md={4}>
            <AlertJauges alertes={data.alertes} />
          </Grid>

          {/* Hotlist impayés - Pleine largeur */}
          <Grid item xs={12}>
            <HotlistImpayes factures={data.hotlistImpayes} />
          </Grid>
        </Grid>
      </Box>

      {/* Floating Action Button - Actions rapides */}
      <DashboardFAB />
    </Container>
  );
};

export default DashboardPage;
